import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { AdminContextSwitcher } from './AdminContextSwitcher';
import { useAuth } from '../../contexts/AuthContext';
import UserProfileModal from './UserProfileModal';
import { getAvatarUrl } from '../../utils/avatarUtils';
import { useAvatar } from '../../hooks/useAvatar';
import { getDisplayName } from '../../utils/nameUtils';

interface ChatbotNavbarProps {
  onOpenMobileNav: () => void;
}

export const ChatbotNavbar: React.FC<ChatbotNavbarProps> = ({ onOpenMobileNav }) => {
  const { user, signOut } = useAuth();
  const { avatarUrl } = useAvatar({ 
    userId: user?.id, 
    avatarBase64: user?.avatarBase64 
  });
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isUserProfileModalOpen, setIsUserProfileModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleSignOut = () => {
    signOut();
    navigate('/login');
  };

  const handleProfileClick = () => {
    setIsProfileMenuOpen(false);
    setIsUserProfileModalOpen(true);
  };

  return (
    <>
      <nav className="w-full bg-blue-900 text-white px-2 sm:px-4 md:px-6 py-3 flex items-center justify-between shadow flex-shrink-0">
        <div className="flex items-center gap-2 md:gap-4 min-w-0">
          <button
            type="button"
            className="md:hidden p-2 -ml-1 rounded-md hover:bg-blue-800"
            onClick={onOpenMobileNav}
            aria-label="Abrir menu"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <Link to="/chatbot-admin/analytics" className="flex items-center gap-2 min-w-0">
            <img src="/know-logo.png" alt="Logo" className="h-8 w-auto" />
            <span className="font-bold text-lg tracking-wide truncate">Administração de Chatbot</span>
          </Link>
          <AdminContextSwitcher active="chatbot" className="ml-4" />
          <div className="hidden lg:flex items-center gap-4 ml-4 border-l border-blue-700 pl-4">
            <Link to="/chatbot-admin/analytics" className="hover:text-blue-200 text-sm">
              Analytics
            </Link>
            <Link to="/chatbot-admin/workspaces" className="hover:text-blue-200 text-sm">
              Chatbots
            </Link>
            <Link to="/chatbot-admin/documents" className="hover:text-blue-200 text-sm">
              Documentos
            </Link>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/" className="hidden sm:inline text-sm hover:text-blue-200">
            Voltar para Aplicação
          </Link>
          <div className="relative">
            <button
              onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
              className="flex items-center gap-2 hover:text-blue-200"
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Avatar"
                  className="h-8 w-8 rounded-full"
                />
              ) : (
                <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                  <span className="text-gray-600 text-sm font-medium">
                    {user?.name ? (() => {
                      const names = user.name.split(' ').filter(n => n.length > 0);
                      if (names.length === 0) return 'U';
                      if (names.length === 1) return names[0][0].toUpperCase();
                      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
                    })() : 'U'}
                  </span>
                </div>
              )}
              <span className="hidden md:inline">{getDisplayName(user?.name)}</span>
            </button>
            
            {isProfileMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 text-gray-700">
                <button
                  onClick={handleProfileClick}
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                >
                  Seu Perfil
                </button>
                <button
                  onClick={handleSignOut}
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                >
                  Sair
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Modal do Perfil do Usuário */}
      <UserProfileModal
        isOpen={isUserProfileModalOpen}
        onClose={() => setIsUserProfileModalOpen(false)}
      />
    </>
  );
};
